/**
 * Tab-local replacement for the sign-in library's cross-tab session lock.
 *
 * By default every open Nightspy tab shares one browser-wide lock and waits for it
 * with no time limit. Phones freeze background tabs, sometimes while they hold that
 * lock, and then every page in the visible tab spins forever. This lock only
 * queues work inside the current tab and never waits longer than `maxWaitMs`.
 * Token refreshes are still kept to one tab by the "most recent tab refreshes" rule
 * in AuthContext.
 */
export type AuthLock = <R>(name: string, acquireTimeout: number, fn: () => Promise<R>) => Promise<R>;

export function createTabLock(maxWaitMs = 5000): AuthLock {
  let tail: Promise<void> = Promise.resolve();
  return async (_name, _acquireTimeout, fn) => {
    const previous = tail;
    let release!: () => void;
    tail = new Promise<void>((r) => { release = r; });
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      previous,
      new Promise<void>((r) => { timer = setTimeout(r, maxWaitMs); }),
    ]);
    if (timer) clearTimeout(timer);
    try {
      return await fn();
    } finally {
      release();
    }
  };
}

/** Swap the lock on an already-created auth client and restart its start-up check with it. */
export function installTabAuthLock(auth: unknown, lock: AuthLock = createTabLock()): void {
  const a = auth as { lock?: AuthLock; initializePromise?: unknown; initialize?: () => Promise<unknown> };
  if (!a || typeof a.initialize !== "function") return;
  a.lock = lock;
  // The constructor already queued start-up behind the shared lock; that wait can
  // hang if a frozen tab holds it, so start again on the tab-local lock.
  a.initializePromise = null;
  void a.initialize().catch(() => {});
}
